import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useRelatedEvents } from './hooks';
import { useAddEventToReportMenuItem, useOpenInNewWindow } from '@/hooks';
import type { ScoredEvent } from '@/types/event';
import { getDetectionLabel } from '@/utils/detection-classes';
import { AppRoutes } from '@/constants/routes';
import type { FeedEmptyStateProps } from './components';

const SEARCH_TYPE_TO_BASED_ON: Record<string, string> = {
    appearance_text: 'appearance',
    appearance_image: 'appearance',
    activity_text: 'activity',
    activity_embedding: 'activity',
    face_image: 'person',
};

interface RelatedEventFeedProps {
    eventId: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

function describeRelatedEvent(scored: ScoredEvent, t: TFunction) {
    if (scored.scores && Object.keys(scored.scores).length > 0) {
        const entries = Object.entries(scored.scores);
        const [dominantType] = entries.reduce((max, entry) =>
            entry[1] > max[1] ? entry : max,
        );
        return t(
            `basedOn.${SEARCH_TYPE_TO_BASED_ON[dominantType] ?? 'unknown'}`,
        );
    }
    return getDetectionLabel(t, scored.event.classId);
}

export function RelatedEventFeed({
    eventId,
    titleContent,
    emptyState,
}: RelatedEventFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const navigate = useNavigate();
    const { openEvent } = useOpenInNewWindow();
    const addToReportItem = useAddEventToReportMenuItem();

    const {
        data: relatedEvents,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
    } = useRelatedEvents({ eventId });

    return (
        <GenericEventFeed
            containerTitle={t('sidebar.relatedEvents')}
            titleContent={titleContent}
            onViewAll={() => navigate(`${AppRoutes.EVENTS}?eventId=${eventId}`)}
            items={relatedEvents}
            isLoading={isLoading}
            emptyState={emptyState}
            itemKey={(scored) => scored.event.id}
            itemEvent={(scored) => scored.event}
            cardTitle={t('tile.relatedEvent')}
            itemDescription={(scored) => describeRelatedEvent(scored, t)}
            itemConfidence={(scored) => scored.confidence}
            itemActionMenuItems={(scored, event) =>
                addToReportItem(event, { confidence: scored.confidence })
            }
            onNavigate={(scored, clickEvent) =>
                openEvent(scored.event.id, clickEvent, {
                    state: {
                        from: 'related-events',
                        sourceId: scored.event.sourceId,
                        feedContext: {
                            type: 'relatedEvent',
                            eventId: scored.event.id,
                        },
                    },
                })
            }
            quickViewContextType="event-feed"
            pagination={{ hasMore, isLoadingMore, onLoadMore: loadMore }}
        />
    );
}
